import React from 'react';
import { FiDownload } from 'react-icons/fi';
import ShieldCheckIcon from '../../../ShieldCheckIcon';
import { WidgetDef, GENERATE_NOTE_SINGLE } from '../types';
import { DemoHead, DemoSearch } from '../shared';

// spec 622:5672
interface GovDoc { title: string; size: string; updated: string; category: 'charters' | 'policies' }

const DOCS: GovDoc[] = [
    { title: 'Board Charter', size: '180 KB', updated: '12 Aug 2026', category: 'charters' },
    { title: 'Audit & Risk Committee Charter', size: '164 KB', updated: '12 Aug 2026', category: 'charters' },
    { title: 'Remuneration & Nomination Committee Charter', size: '152 KB', updated: '4 Jul 2026', category: 'charters' },
    { title: 'Continuous Disclosure Policy', size: '120 KB', updated: '30 Jun 2026', category: 'policies' },
    { title: 'Securities Trading Policy', size: '138 KB', updated: '30 Jun 2026', category: 'policies' },
    { title: 'Diversity & Inclusion Policy', size: '110 KB', updated: '18 Mar 2026', category: 'policies' },
];

const corporateGovernance: WidgetDef = {
    slug: 'corporate-governance',
    name: 'Corporate governance',
    description: 'Charters, policies and statements',
    icon: <ShieldCheckIcon />,
    title: 'Corporate Governance Widget',
    installed: false,
    page: null,
    mustInstall: true,
    views: null,
    demoPath: '/investors/',
    addNote: 'This code is generated from the settings below. Change a setting and the code updates — paste it once, it stays current.',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_governance instance="governance" filter="tabs"]' },
        { title: '2 · Or plain HTML', code: '<div data-investi-instance="governance" data-investi="governance"\n     data-investi-filter="tabs"></div>', dark: true },
        {
            title: '3 · Or one part at a time',
            codes: [
                { label: 'Charters', code: '[investi_governance category="charters"]' },
                { label: 'Policies & Procedures', code: '[investi_governance category="policies"]' },
                { label: 'Governance Statement', code: '[investi_governance block="statement"]' },
            ],
            after: 'Use these when a category or the statement sits on its own page — each one keeps its own settings.',
        },
    ],
    settings: [
        { kind: 'heading', label: 'Categories' },
        { kind: 'toggle', key: 'charters', label: 'Charters', default: true },
        { kind: 'toggle', key: 'policies', label: 'Policies & Procedures', default: true },
        { kind: 'segmented', key: 'filter', label: 'Filter', options: ['Tabs', 'Dropdown', 'No filter'], default: 'Tabs', inline: true },
        { kind: 'heading', label: 'Governance statement' },
        { kind: 'toggle', key: 'statement', label: 'Include the governance statement', hint: 'Set in Website Hub › Corporate Governance', default: true },
        // a plain note: the spec has no icon here
        { kind: 'custom', render: () => <div className="wd-info">The statement sits above the list as its own card. It can also go on a page by itself — see “3 · Or One Part at a Time”.</div> },
        { kind: 'heading', label: 'What each row shows' },
        { kind: 'toggle', key: 'updated', label: 'Updated date', default: true },
        { kind: 'toggle', key: 'newTab', label: 'Open documents in a new tab', default: true },
    ],
    generateNote: GENERATE_NOTE_SINGLE,
    Demo: ({ settings: s }) => {
        const tabs = [s.charters && 'Charters', s.policies && 'Policies & Procedures'].filter(Boolean) as string[];
        const docs = DOCS.filter(d => !!s[d.category]);
        return <div className="wd-governance">
            <DemoHead title="Corporate governance" sub="Charters, policies and the governance statement" />
            {s.statement && <div className="wd-gov-statement">
                <div className="t"><strong>Corporate Governance Statement 2026</strong><span>Approved by the Board · Updated 30 Jun 2026</span></div>
                <span className="btn">View the Statement</span>
            </div>}
            {s.filter === 'Tabs' && <div className="wd-gov-tabs">
                <span className="active">All</span>
                {tabs.map(t => <span key={t}>{t}</span>)}
            </div>}
            {s.filter === 'Dropdown' && <DemoSearch placeholder="" showSearch={false} filters={['Category: All']} />}
            <div className="wd-gov-docs">
                {docs.map(d => <div key={d.title} className="row">
                    <div className="t"><strong>{d.title}</strong><span>PDF · {d.size}{s.updated && ` · Updated ${d.updated}`}</span></div>
                    <span className={`tag ${d.category}`}>{d.category === 'charters' ? 'Charter' : 'Policy'}</span>
                    <FiDownload className="dl" />
                </div>)}
            </div>
        </div>;
    },
};

export default corporateGovernance;
